import { websocket, ConnectionMessageType, WebsocketMessage, SystemMessageType } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { handleAuthMessage } from './handlers/authMessage';
import { handleConnectionMessage } from './handlers/connectionMessage';
import { handleInvitationMessage } from './handlers/invitationMessage';
import { stampOutbound } from '../playerObservability';

const HEARTBEAT_INTERVAL = 10000; // 10 seconds between pings
const RECONNECT_TIMEOUT = 3000; // 3 seconds before reconnecting

let wsConnection: WebSocket | null = null;
let isReady = false;
let messageQueue: WebsocketMessage[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;

export const getIsReady = () => {
  return isReady;
};

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      stopHeartbeat();
      return;
    }

    try {
      const pingMessage = websocket.generators.system(SystemMessageType.SYS_PING, {});
      wsConnection.send(JSON.stringify(stampOutbound(pingMessage)));
    } catch (error) {
      console.error('[Realtime] Failed to send heartbeat:', error);
      disconnect();
      handleReconnect();
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const handleReconnect = () => {
  if (isReconnecting) {
    console.log('[Realtime] Reconnection already in progress');
    return;
  }

  isReconnecting = true;
  disconnect();
  clearReconnectTimeout();

  reconnectTimeout = setTimeout(() => {
    try {
      wsConnection = null;
      connect(isObserver);
    } catch (error) {
      console.error('[Realtime] Reconnection attempt failed:', error);
    } finally {
      isReconnecting = false;
    }
  }, RECONNECT_TIMEOUT);
};

export let isObserver = false;

export const connect = (observer?: boolean) => {
  isObserver = observer ?? false;
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Realtime] Connection already exists');
    return;
  }

  try {
    wsConnection = new WebSocket(constants.REALTIME_API_WEBSOCKET_URL);

    wsConnection.onopen = () => {
      setTimeout(() => {
        if (wsConnection?.readyState === WebSocket.OPEN) {
          console.log('[Realtime] Connected, sending init message');
          const msg = websocket.generators.connection(ConnectionMessageType.CONN_INIT, {
            version: constants.VERSION,
          });
          wsConnection?.send(JSON.stringify(stampOutbound(msg)));
          startHeartbeat();
        } else {
          console.warn('[Realtime] Connection not ready after open event');
          handleReconnect();
        }
      }, 100);
    };

    wsConnection.onmessage = (event) => {
      const validation = websocket.validateWebsocketMessage(event.data);
      if (!validation.success) {
        console.error('[Realtime] Invalid message:', validation.error);
        return;
      }

      const message = validation.data;
      if (!message) {
        console.error('[Realtime] No message data');
        return;
      }

      try {
        if (websocket.isSystemMessage(message)) {
          if (message.type === SystemMessageType.SYS_ERROR) {
            console.error('[Realtime] System error:', message.error);
          }
        } else if (websocket.isAuthMessage(message)) {
          handleAuthMessage(message);
        } else if (websocket.isConnectionMessage(message)) {
          handleConnectionMessage(message, wsConnection);
        } else if (websocket.isInvitationMessage(message)) {
          handleInvitationMessage(message);
        }
      } catch (e) {
        console.error('[Realtime] Failed to handle message:', e);
      }
    };

    wsConnection.onclose = () => {
      console.log('[Realtime] WebSocket closed');
      isReady = false;
      stopHeartbeat();

      if (!isReconnecting) {
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Realtime] WebSocket error:', error);
    };
  } catch (error) {
    console.error('[Realtime] Failed to connect:', error);
    handleReconnect();
  }
};

export const disconnect = () => {
  stopHeartbeat();
  clearReconnectTimeout();
  isReady = false;
  messageQueue = [];

  if (wsConnection) {
    const conn = wsConnection;
    wsConnection = null;

    try {
      conn.close();
    } catch (error) {
      console.error('[Realtime] Error while closing connection:', error);
    }
  }
};

export const setIsReady = (ready: boolean) => {
  isReady = ready;
  if (isReady) {
    processQueue();
  }
};

const processQueue = () => {
  while (messageQueue.length > 0 && isReady && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        wsConnection.send(JSON.stringify(stampOutbound(message)));
      } catch (error) {
        console.error('[Realtime] Failed to send queued message:', error);
        messageQueue.unshift(message);
        break;
      }
    }
  }
};

export const sendMessage = (message: WebsocketMessage) => {
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isReady) {
    console.warn('[Realtime] Connection not ready, queueing message');
    messageQueue.push(message);
    return;
  }

  try {
    wsConnection.send(JSON.stringify(stampOutbound(message)));
  } catch (error) {
    console.error('[Realtime] Failed to send message:', error);
    messageQueue.push(message);
  }
};
